import { Injectable, signal } from '@angular/core';

export type ToastVariant = 'error' | 'success' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title?: string;
  message: string;
}

/** Signal-based port of Consumer's core/components/Toast.tsx `useToastStore` (Zustand). */
@Injectable({ providedIn: 'root' })
export class ToastService {
  readonly toasts = signal<ToastItem[]>([]);
  private timers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * @param titleOrMessage - When `message` is omitted, treated as the body only (no title).
   *   When both are passed: optional title + message (Figma layout).
   */
  private add(
    variant: ToastVariant,
    titleOrMessage: string,
    message?: string,
    durationMs = 5000,
  ): void {
    const id = crypto.randomUUID();
    const hasTitle = message != null;
    const title = hasTitle ? titleOrMessage : undefined;
    const body = hasTitle ? message : titleOrMessage;
    this.toasts.update((t) => [...t, { id, variant, title, message: body }]);
    const timer = setTimeout(() => {
      this.timers.delete(id);
      this.toasts.update((t) => t.filter((x) => x.id !== id));
    }, durationMs);
    this.timers.set(id, timer);
  }

  /** `error(message)` or `error(title, message)` */
  error(titleOrMessage: string, message?: string): void {
    this.add('error', titleOrMessage, message);
  }

  /** `success(message)` or `success(title, message)` */
  success(titleOrMessage: string, message?: string): void {
    this.add('success', titleOrMessage, message);
  }

  /** `warning(message)` or `warning(title, message)` */
  warning(titleOrMessage: string, message?: string): void {
    this.add('warning', titleOrMessage, message);
  }

  /** `info(message)` or `info(title, message)` */
  info(titleOrMessage: string, message?: string): void {
    this.add('info', titleOrMessage, message);
  }

  dismiss(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
    this.toasts.update((t) => t.filter((x) => x.id !== id));
  }
}
